import { createStore } from 'devextreme-aspnet-data-nojquery';
import { getApiErrorMessage, handleConcurrencyConflict } from './concurrency';
import { MESSAGES } from './Messages';

/**
 * The standard `onConflict` for a DevExtreme grid: drop the rejected edit, re-pull the rows, then run
 * anything derived from them.
 *
 * Discarding the rejected edit is not optional — without it the cell stays stuck in its failed-edit
 * state, and worse, DevExtreme re-saves it. See `discardPendingEdits`.
 *
 * Pass only *derived* refreshers in `after` (the receipt screens' `changeTotals`). Never pass
 * something that refetches state the user owns: reloading a header would silently discard unsaved
 * header edits, and a detail conflict says nothing about the header anyway.
 *
 * @param {object} gridRef  React ref to the DataGrid. Read lazily, so it may still be empty here.
 * @param {...Function} after  derived refreshers to run once the grid has reloaded.
 */
export function refreshGridOnConflict(gridRef, ...after) {
    const refresh = async () => {
        const grid = gridRef?.current?.instance?.();
        if (!grid) return;

        discardPendingEdits(grid);
        await grid.refresh();

        after.forEach((fn) => { if (typeof fn === 'function') fn(); });
    };

    // Read by createConcurrencyAwareStore so it can drop the rejected edit the instant the conflict is
    // detected. Waiting until the button click is too late — see discardPendingEdits.
    refresh.gridRef = gridRef;
    return refresh;
}

/**
 * Throws away a pending edit **without** letting DevExtreme try to save it again.
 *
 * Exported because a user-initiated Cancel needs exactly the same treatment as a rejected edit:
 * the hazard below is a property of cell edit mode, not of the conflict path.
 *
 * Do not use `cancelEditData()` or `closeEditCell()` here. In cell edit mode both route through
 * `_closeEditCellCore`, which does:
 *
 *     if (this.isCellEditMode() && this.hasChanges()) { ... this.saveEditData() ... }
 *
 * The rejected edit is still sitting in `editing.changes`, so that re-submits the exact request the
 * server just refused. DevExtreme's own click-outside handler (`_closeEditItem`) takes the same path,
 * which is why simply clicking the dialog's button fired a second save. Clearing the options
 * declaratively bypasses all of it.
 */
export function discardPendingEdits(grid) {
    if (!grid) return;

    grid.option('editing.changes', []);
    grid.option('editing.editRowKey', null);
    grid.option('editing.editColumnName', null);
}

/** Wraps a grid's `onConflict` into the dialog action, or undefined to keep the default reload. */
function conflictAction(onConflict) {
    return onConflict ? { label: MESSAGES.REFRESH, onConfirm: onConflict } : undefined;
}

/**
 * `createStore` with the boilerplate every grid repeats, plus optimistic-concurrency handling.
 *
 * Grids do not go through axios — `devextreme-aspnet-data-nojquery` issues its own requests — so
 * the global response interceptor in AppContainer never sees them. `onAjaxError` is the only hook
 * the package exposes, and it can also rewrite the message DevExtreme puts in the grid's error row.
 *
 * Everything else is passed straight through, so the standard DevExtreme form-encoded protocol is
 * untouched: no body rewriting in `onBeforeSend`, keys and parent FKs still stamped by the grid's
 * `onRowInserting` / `onRowUpdating`.
 *
 * @param {object} options   createStore options (loadUrl, insertUrl, key, loadParams, …), plus the
 *   extra `onConflict` below. Everything else is passed through to `createStore` untouched.
 * @param {Function} [options.onConflict]  re-pull this grid instead of reloading the page when a save
 *   is rejected as stale. Build it with `refreshGridOnConflict(gridRef, ...derivedRefreshers)` rather
 *   than by hand — the store also reads `onConflict.gridRef` to discard the rejected edit at detection
 *   time. Do **not** pass anything that refetches state the user owns: reloading a header would
 *   discard unsaved edits, and a detail conflict implies nothing about the header. Omit it entirely to
 *   keep the default full-page reload.
 * @param {string} [accessToken]  bearer token for the default auth headers. Optional — omit it when
 *   the caller's own `onBeforeSend` already sets them, which is the case on the older WM screens.
 */
export function createConcurrencyAwareStore(options, accessToken) {
    // onConflict is ours, not createStore's — it must not be forwarded.
    const { onBeforeSend, onAjaxError, onConflict, ...rest } = options || {};

    return createStore({
        ...rest,

        onBeforeSend: (method, ajaxOptions) => {
            if (accessToken) {
                ajaxOptions.headers = {
                    ...(ajaxOptions.headers || {}),
                    Authorization: `Bearer ${accessToken}`,
                    access_token: accessToken
                };
            }

            if (onBeforeSend) onBeforeSend(method, ajaxOptions);
        },

        onAjaxError: (e) => {
            // handleConcurrencyConflict detects and returns whether it handled it, so there is no
            // need to test with isConcurrencyConflict first.
            if (handleConcurrencyConflict(e.xhr, conflictAction(onConflict))) {
                // Synchronously, in the same tick the dialog is raised and long before the user can
                // click anything: while the rejected edit remains in `editing.changes`, ANY path that
                // closes the cell re-saves it — including DevExtreme's click-outside handler reacting
                // to the click on the dialog itself.
                discardPendingEdits(onConflict?.gridRef?.current?.instance?.());

                // Must stay a NON-EMPTY string. devextreme-aspnet-data does
                //     if (error) d.reject(error); else d.reject(xhr, textStatus);
                // so blanking this makes it reject with the raw XMLHttpRequest, which the grid
                // stringifies into "[object XMLHttpRequest]" in its error row. There is no way to
                // suppress that row from here, so give it the real message instead — it matches the
                // dialog and both disappear on reload.
                e.error = getApiErrorMessage(e.xhr, MESSAGES.RECORD_CHANGED_BY_ANOTHER_USER);
            }

            if (onAjaxError) onAjaxError(e);
        }
    });
}

/**
 * Message extraction for a store rejection. `devextreme-aspnet-data-nojquery` rejects with a plain
 * string, so the usual `err.response.data.message` ladder does not apply.
 * Returns null when the failure was a concurrency conflict that has already raised the dialog —
 * the caller should stay silent rather than stack a red toast on top of it.
 *
 * @param {Function} [onConflict]  pass the **same** action the store was built with. `remove()`
 *   bypasses the grid's error row, so deletes are handled here rather than in `onAjaxError`; without
 *   this a stale delete would reload the whole page while a stale edit on the same grid only
 *   refreshed it.
 */
export function getStoreErrorMessage(error, fallback, onConflict) {
    if (handleConcurrencyConflict(error, conflictAction(onConflict))) return null;

    return (
        (typeof error === 'string' ? error : null) ||
        error?.message ||
        error?.responseJSON?.message ||
        fallback ||
        MESSAGES.ERROR
    );
}
